import { Injectable } from '@angular/core';
import axios, { InternalAxiosRequestConfig } from 'axios';
import { TranslationService } from '@ace/ace';

/**
 * Sidebar menu labels come from /ace-common/api/menus over axios, so they never reach
 * the Angular http interceptors and are rendered without a translate pipe. This hooks
 * the axios response and swaps each pageName for its entry in translation_<lang>.json.
 * The menu-management screens call the same endpoint with query params - those responses
 * are left untouched so saved menu names stay in English.
 */
@Injectable({ providedIn: 'root' })
export class MenuTranslationInterceptor {
  private static readonly SIDEBAR_MENU_ENDPOINT = '/ace-common/api/menus';

  constructor(private translation: TranslationService) {}

  register(): void {
    axios.interceptors.response.use(response => {
      if (this.isSidebarMenuRequest(response.config) && Array.isArray(response.data?.data)) {
        response.data = { ...response.data, data: this.translateMenus(response.data.data) };
      }
      return response;
    });
  }

  private isSidebarMenuRequest(config: InternalAxiosRequestConfig): boolean {
    const path = String(config?.url ?? '').split('?')[0];
    return path.endsWith(MenuTranslationInterceptor.SIDEBAR_MENU_ENDPOINT) && !config?.params;
  }

  private translateMenus(menus: any[]): any[] {
    return menus.map(menu => ({
      ...menu,
      pageName: typeof menu?.pageName === 'string' && menu.pageName.trim()
        ? this.translation.translate(menu.pageName)
        : menu?.pageName,
      children: Array.isArray(menu?.children) ? this.translateMenus(menu.children) : menu?.children,
    }));
  }
}
